import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../components/layout/Breadcrumb";
import { fetchAllTickets, fetchAllCategories } from "../../../../redux/slice/supportSlice";
import { useHasPermission } from "../../../../hooks/useHasPermission";

export default function HRTicketDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tickets = [], loading, total } = useSelector(
    (state) => state.support
  );

  const currentUser = useSelector((state) => state.auth?.user);
  const InitialPermissionData = useSelector(
    (state) => state.persistedReducer?.forms?.savedPermissions
  );
  const loggedInUserId = InitialPermissionData?.userId || currentUser?.id;

  const canView = useHasPermission("Support-view");
  const canManage = useHasPermission("Support-manage");

  const canAllViewTickets = useHasPermission("Support-all-view");
  const canAllManageTickets = useHasPermission("Support-all-manage");

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const { categories = [] } = useSelector((state) => state.support);
  const currentRole = localStorage.getItem("role");
  const isSuperAdmin =
    currentRole === "SUPER_ADMIN" ||
    currentUser?.role === "SUPER_ADMIN" ||
    [
      "superadmin-hrms@moptra.com",
      "superadmin@moptra.com",
    ].includes(currentUser?.email);

  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  // date format
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB");
  };

  const currentUserId = useSelector(
    (state) => state.persistedReducer.forms.savedPermissions?.userId
  );

  const [page, setPage] = useState(0);
  const size = 15;

  const [jumpPage, setJumpPage] = useState("");
  /* ================= FETCH ================= */
  useEffect(() => {
    dispatch(
      fetchAllTickets({
        status: statusFilter === "ALL" ? "" : statusFilter,
        category: categoryFilter === "ALL" ? "" : categoryFilter,
        search,
        page,
        size,
      })
    );
  }, [dispatch, statusFilter, categoryFilter, search, page]);
  /* ================= RESET PAGE ================= */
  useEffect(() => {
    setPage(0);
  }, [statusFilter, categoryFilter, search]);
  /* ================= FORMAT ================= */
  const formatLabel = (value) => {
    if (!value) return "-";
    return value
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/,/g, ", ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  /* ================= BADGES ================= */
  const StatusBadge = ({ status }) => {
    const getStatusClass = () => {
      switch (status) {
        case "CREATED":
          return "status-open";
        case "APPROVED":
          return "status-resolved";
        case "UPDATED":
          return "status-in-progress";
        case "REWORK_REQUIRED":
          return "status-open";
        case "CLOSE":
          return "status-open";
        default:
          return "status-open";
      }
    };

    return (
      <span className={`badge status-badge ${getStatusClass()}`}>
        {formatLabel(status)}
      </span>
    );
  };

  const PriorityBadge = ({ priority }) => {
    const getPriorityClass = () => {
      switch (priority?.toLowerCase()) {
        case "critical":
        case "high":
          return "priority-high";
        case "medium":
          return "priority-medium";
        case "low":
          return "priority-low";
        default:
          return "priority-medium";
      }
    };

    return (
      <span className={`badge priority-badge ${getPriorityClass()}`}>
        {priority}
      </span>
    );
  };

  const totalPages = Math.ceil((total || 0) / size);

  /* ================= FILTER ================= */
  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.title?.toLowerCase().includes(search.toLowerCase());
    if (canAllViewTickets) {
      return matchesSearch;
    }
    return (
      matchesSearch && Number(ticket.assignedToUserId) === Number(currentUserId)
    );
  });

  return (
    <div className="container-fluid">
      <div className=" d-flex justify-content-between align-items-center" style={{ flexDirection: "row-reverse", marginBottom: "18px" }}>
        <div className="icons-list">
          <Breadcrumb />
        </div>
      </div>

      <div className="section-body m-0">
        <div className="container-fluid">

          {/* Header */}
          <div className="page-header">
            <div className="header-content">
              <h1>HR Ticket Dashboard</h1>
            </div>
          </div>

          {/* FILTERS */}
          <div className="filters-bar">
            <div className="filter-group">
              <div className="filter-icon">
                <i className="bi bi-grid"></i>
              </div>

              <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="ALL">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name
                      .toLowerCase()
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <div className="filter-icon"><i className="bi bi-funnel"></i></div>
              <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">-- All Status --</option>
                <option value="CREATED">Created</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
                <option value="APPROVED">Approved</option>
                <option value="UPDATED">Updated</option>
                <option value="REJECTED">Rejected</option>
                <option value="REWORK_REQUIRED">Rework Required</option>
                <option value="VERIFIED">Verified</option>
                <option value="ESCALATED">Escalated</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
          </div>
          <div className="card">
            <div className="tickets-page">
              <div className="tickets-container">
                {/* Loader */}
                {loading && (
                  <div className="text-center text-gray-500" style={{ padding: "2rem" }}>
                    <i className="bi bi-hourglass-split me-2"></i>
                    Loading Tickets...
                  </div>
                )}
                {/* Empty State */}
                {!loading && filteredTickets.length === 0 && (
                  <div className="no-tickets server-error-msg text-center">
                    <i className="bi bi-inbox display-4"></i>
                    <p className="mt-2">-- No tickets found --</p>
                    <button className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={() => {
                        setSearch("");
                        setFilter("ALL");
                      }}>
                      Reset Filters
                    </button>
                  </div>
                )}

                {/* Tickets List (UNCHANGED UI) */}
                <div className="tickets-list">
                  {!loading &&
                    filteredTickets.map((ticket) => {
                      const canReviewTicket = canAllManageTickets || Number(ticket.assignedToUserId) === Number(currentUserId);
                      //console.log('===========', Number(ticket.assignedToUserId), '===', Number(currentUserId))
                      return (
                        <div key={ticket.id} className="ticket-card">
                          <div className="ticket-info">
                            <div className="title-row mb-2">
                              <span className="ticket-id">
                                <label className="fs-50 font-weight-bold">Ticket ID: </label>
                                #TKT-{ticket.id.toString().padStart(3, "0")}
                              </span>
                              <div className="mb-2">
                                <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                  Creator Name:
                                </span>
                                <span className="text-dark meta-value">
                                  {ticket.createdByName}
                                </span>
                              </div>
                              <div className="mb-2">
                                <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                  Assignee name:
                                </span>
                                <span className="text-dark meta-value">
                                  {ticket.assignedToName}
                                </span>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-2">
                                  <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                    Title:
                                  </span>
                                  <span className="text-dark meta-value">
                                    {ticket.title}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                    Category:
                                  </span>
                                  <span className="text-dark meta-value">
                                    {formatLabel(ticket.category)}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="d-flex gap-20">
                                  <div className="mb-2">
                                    <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                      Status:
                                    </span>
                                    <StatusBadge status={ticket.status} />
                                  </div>
                                  <div className="mb-2">
                                    <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                      Priority:
                                    </span>
                                    <PriorityBadge priority={ticket.priority} />
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-12">
                                {ticket.category === "PROFILE_UPDATE" && (
                                  <div className="mb-2">
                                    <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                      Sub Category:
                                    </span>
                                    <span className="text-dark meta-value">
                                      <em>{formatLabel(ticket.subCategories)}</em>
                                    </span>
                                  </div>
                                )}
                                <div className="mb-2">
                                  <span className="font-weight-bold text-secondary-dark me-2 fs-50">
                                    Description:
                                  </span>
                                  <span className="text-theme meta-value">
                                    {
                                      ticket.description?.length > 120
                                        ? `${ticket.description.slice(0, 120)}...`
                                        : ticket.description || "-"
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="ticket-action">
                            {canReviewTicket && (
                              <button className="btn-view-details"
                                onClick={() => {
                                  const canOpenTicket =
                                    isSuperAdmin ||
                                    canAllManageTickets ||
                                    Number(ticket.assignedToUserId) === Number(loggedInUserId);
                                  if (!canOpenTicket) {
                                    toast.error("You are not authorized to view this ticket");
                                    return;
                                  }
                                  navigate(`/admin/support/hr/ticket/${ticket.id}`);
                                }}>
                                <i className="bi bi-eye"></i>
                                Review
                              </button>
                            )}
                            <span className="ticket-date">
                              <em className="font-weight-bold">Created at: </em> {formatDate(ticket.createdAt)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {totalPages > 1 && (
                  <div className="pagination-container d-flex justify-content-center mt-3">
                    <button className="btn btn-light me-2 btn-left" disabled={page === 0} onClick={() => setPage(page - 1)} >
                      &laquo;
                    </button>
                    <span className="btn-number align-self-center">
                      {page + 1} of {totalPages}
                    </span>
                    <button className="btn btn-light ms-2 btn-right" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} >
                      &raquo;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}